import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { FileViewer } from '@capacitor/file-viewer';
import { Share } from '@capacitor/share';
import { getValidFirmAccessToken } from '../auth/firmSession';
import { getValidClientAccessToken } from '../auth/clientSession';
import { randomUuid } from './identifiers';

const blobs = new Map();
function blobBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
export async function clearNativeDocumentCache() {
  if (!Capacitor.isNativePlatform()) return;
  blobs.clear();
  try {
    const { files } = await Filesystem.readdir({ directory: Directory.Cache, path: '' });
    await Promise.all(files.filter(file => file.name.startsWith('portal-')).map(file => Filesystem.deleteFile({ directory: Directory.Cache, path: file.name }).catch(() => {})));
  } catch { /* The OS may have already removed its cache. */ }
}
export async function openNativeDocument(source, filename = '') {
  let blob = source instanceof Blob ? source : blobs.get(String(source));
  let remoteUrl;
  let headers = {};
  if (!blob) {
    const url = new URL(String(source), window.location.origin);
    if (url.protocol !== 'https:' && url.protocol !== 'blob:') throw new Error('This document link cannot be opened securely.');
    const api = new URL(process.env.REACT_APP_API_URL || '/api', window.location.origin);
    if (url.origin === api.origin) {
      const token = localStorage.getItem('clientToken') ? await getValidClientAccessToken() : await getValidFirmAccessToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    }
    filename = filename || decodeURIComponent(url.pathname.split('/').pop() || '');
    if (!/\.[a-z0-9]{2,5}$/i.test(filename)) filename = 'Document.pdf';
    if (url.protocol === 'https:') remoteUrl = url.href;
    else {
      const response = await fetch(url.href);
      if (!response.ok) throw new Error('The generated document is no longer available. Generate it again.');
      blob = await response.blob();
    }
  }
  filename = String(filename || (blob?.type === 'application/pdf' ? 'Document.pdf' : 'Document')).replace(/[^a-zA-Z0-9._ -]/g, '_').slice(-140);
  if (blob?.type === 'application/pdf' && !/\.pdf$/i.test(filename)) filename += '.pdf';
  const path = `portal-${randomUuid()}-${filename}`;
  let uri;
  if (remoteUrl) {
    // The native download does not require the storage bucket to allow WebView
    // origins and streams large packets without base64 copies in JavaScript.
    const file = await Filesystem.downloadFile({ directory: Directory.Cache, path, url: remoteUrl, headers });
    uri = file.path;
  } else {
    const file = await Filesystem.writeFile({ directory: Directory.Cache, path, data: await blobBase64(blob) });
    uri = file.uri;
  }
  if (/\.pdf$/i.test(filename) || blob?.type === 'application/pdf') await FileViewer.openDocumentFromLocalPath({ path: uri });
  else await Share.share({ title: filename, files: [uri], dialogTitle: 'Save or share document' });
}
function report(error) { window.alert(error.message || 'This file could not be opened. Please try again.'); }
function isFileUrl(value) { return /^(?:blob:|data:)/i.test(value) || /\.(?:pdf|docx?|xlsx?|zip|txt|png|jpe?g)(?:[?#]|$)/i.test(value) || /\/uploaded-document\/[^/]+\/raw\//.test(value) || /(?:X-Amz-Signature|response-content-disposition)=/i.test(value); }
async function openSource(value, filename) {
  const raw = String(value || '');
  if (isFileUrl(raw) || filename) return openNativeDocument(raw, filename);
  const parsed = new URL(raw, window.location.origin);
  if (parsed.origin === window.location.origin) { window.location.assign(parsed.href); return; }
  if (parsed.protocol === 'https:') return Browser.open({ url: parsed.href });
  if (['mailto:', 'tel:'].includes(parsed.protocol)) { window.location.assign(parsed.href); return; }
  throw new Error('This link cannot be opened in the app.');
}
export function installNativeFileHandling() {
  if (!Capacitor.isNativePlatform()) return;
  const create = URL.createObjectURL.bind(URL);
  URL.createObjectURL = blob => { const url = create(blob); blobs.set(url, blob); return url; };
  const revoke = URL.revokeObjectURL.bind(URL);
  URL.revokeObjectURL = url => { revoke(url); setTimeout(() => blobs.delete(url), 60000); };
  const originalClick = HTMLAnchorElement.prototype.click;
  HTMLAnchorElement.prototype.click = function click() {
    if (this.download || isFileUrl(this.href)) { openSource(this.href, this.download).catch(report); return; }
    originalClick.call(this);
  };
  document.addEventListener('click', event => {
    const anchor = event.target.closest?.('a');
    if (!anchor || !anchor.href) return;
    const parsed = new URL(anchor.href, window.location.origin);
    if (anchor.download || isFileUrl(anchor.href) || (anchor.target === '_blank' && parsed.origin !== window.location.origin)) { event.preventDefault(); openSource(anchor.href, anchor.download).catch(report); }
  }, true);
  window.open = value => {
    if (value && value !== 'about:blank') openSource(value).catch(report);
    const location = { set href(url) { openSource(url).catch(report); }, replace: url => openSource(url).catch(report), assign: url => openSource(url).catch(report) };
    return { closed: false, close() { this.closed = true; }, focus() {}, get location() { return location; }, set location(url) { openSource(url).catch(report); }, document: { write() {}, close() {} } };
  };
}
